/**
 * Sprint C — Modale "AMO commente DPGF".
 *
 * Permet au moe_principal/moe_membre/archi :
 *  - Commenter un poste DPGF précis ("comment")
 *  - Demander l'ajout d'un nouveau poste ("demande_ajout") avec désignation proposée
 *
 * Génère automatiquement une tâche pour l'archi côté backend (POST /dpgf/commentaire).
 */
import { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X, ChatCircleDots, Plus } from '@phosphor-icons/react';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AmoCommentBox({
  open,
  onClose,
  chantierId,
  posteId,         // optionnel (si commentaire sur poste existant)
  posteDesignation,
  lotId,
  lotNom,
  defaultType = 'comment', // 'comment' | 'demande_ajout'
  onCreated,
}) {
  const [type, setType] = useState(defaultType);
  const [designation, setDesignation] = useState('');
  const [commentaire, setCommentaire] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setType(defaultType);
      setDesignation('');
      setCommentaire('');
    }
  }, [open, defaultType]);

  if (!open) return null;

  const send = async () => {
    if (submitting) return;
    if (!commentaire.trim()) { toast.error('Commentaire requis'); return; }
    if (type === 'demande_ajout' && !designation.trim()) {
      toast.error('Désignation proposée requise');
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        type,
        poste_id: type === 'comment' ? posteId : null,
        lot_id: lotId,
        designation_proposee: type === 'demande_ajout' ? designation.trim() : null,
        commentaire: commentaire.trim(),
      };
      const { data } = await axios.post(
        `${API_URL}/api/chantiers/${chantierId}/dpgf/commentaire`,
        payload,
        { withCredentials: true },
      );
      toast.success(data.message || 'Commentaire envoyé');
      onCreated?.(data);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur envoi commentaire');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
      onClick={onClose}
      data-testid="amo-comment-box"
    >
      <div
        className="bg-white shadow-2xl w-full max-w-md max-h-full overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-4 border-b border-[#E0E0DB] flex items-center justify-between bg-[#F5F0FB]">
          <div>
            <h3 className="text-base font-bold text-[#2C2C2C]" data-testid="amo-comment-title">
              Commentaire DPGF
            </h3>
            <p className="text-xs text-[#6B6B6B] mt-0.5">
              Une action sera créée pour l'architecte.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-white rounded-none"
            aria-label="Fermer"
            data-testid="amo-comment-close"
          >
            <X size={18} />
          </button>
        </div>

        <div className="p-4 space-y-3 text-sm">
          {/* Type */}
          <div>
            <div className="text-[10px] uppercase tracking-wider text-[#6B6B6B] font-bold mb-1">Type</div>
            <div className="grid grid-cols-2 gap-2">
              <button
                onClick={() => setType('comment')}
                disabled={!posteId}
                className={`flex items-center justify-center gap-1 px-2 py-2 text-xs border rounded-none ${
                  type === 'comment'
                    ? 'bg-[#7B5EA7] text-white border-[#7B5EA7]'
                    : 'bg-white text-[#2C2C2C] border-[#E0E0DB] hover:border-[#7B5EA7] disabled:opacity-50 disabled:cursor-not-allowed'
                }`}
                data-testid="amo-comment-type-comment"
                title={!posteId ? 'Sélectionnez un poste pour le commenter' : undefined}
              >
                <ChatCircleDots size={14} />
                Commenter le poste
              </button>
              <button
                onClick={() => setType('demande_ajout')}
                className={`flex items-center justify-center gap-1 px-2 py-2 text-xs border rounded-none ${
                  type === 'demande_ajout'
                    ? 'bg-[#7B5EA7] text-white border-[#7B5EA7]'
                    : 'bg-white text-[#2C2C2C] border-[#E0E0DB] hover:border-[#7B5EA7]'
                }`}
                data-testid="amo-comment-type-demande-ajout"
              >
                <Plus size={14} />
                Demander un ajout
              </button>
            </div>
          </div>

          {/* Contexte */}
          <div className="bg-[#FAFAF8] border-l-4 border-[#7B5EA7] p-2 text-xs text-[#4B3A6B]">
            <div className="font-semibold">Lot : {lotNom || '—'}</div>
            {type === 'comment' && posteDesignation && (
              <div className="text-[#6B6B6B]">Poste : {posteDesignation}</div>
            )}
          </div>

          {/* Désignation proposée (demande_ajout) */}
          {type === 'demande_ajout' && (
            <div>
              <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">
                Désignation proposée
              </label>
              <input
                value={designation}
                onChange={(e) => setDesignation(e.target.value)}
                placeholder="Ex : Doublage acoustique cloison cuisine"
                maxLength={400}
                className="w-full px-3 py-2 border border-[#E0E0DB] rounded-none text-sm focus:outline-none focus:border-[#7B5EA7]"
                data-testid="amo-comment-designation"
              />
            </div>
          )}

          {/* Commentaire */}
          <div>
            <label className="block text-[10px] uppercase tracking-wider text-[#6B6B6B] mb-1 font-bold">
              Commentaire / justification
            </label>
            <Textarea
              rows={4}
              value={commentaire}
              onChange={(e) => setCommentaire(e.target.value)}
              maxLength={4000}
              placeholder={
                type === 'comment'
                  ? 'Précisez votre observation…'
                  : 'Justifiez la demande d\'ajout pour l\'architecte…'
              }
              className="rounded-none focus:border-[#7B5EA7]"
              data-testid="amo-comment-textarea"
            />
          </div>
        </div>

        <div className="p-4 border-t border-[#E0E0DB] flex justify-end gap-2 bg-[#FAFAF8]">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={onClose}
            disabled={submitting}
            data-testid="amo-comment-cancel"
          >
            Annuler
          </Button>
          <Button
            className="rounded-none bg-[#7B5EA7] hover:bg-[#5e4585] text-white"
            onClick={send}
            disabled={submitting || !commentaire.trim() || (type === 'demande_ajout' && !designation.trim())}
            data-testid="amo-comment-send"
          >
            {submitting ? 'Envoi…' : 'Envoyer'}
          </Button>
        </div>
      </div>
    </div>
  );
}
